/**
 * OAuth Callback Page
 * 
 * Handles OAuth redirect from backend after Google authentication
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './AuthCallback.css';

const AuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(true);
  const hasProcessed = useRef(false);
  const loginWithTokenRef = useRef(loginWithToken);

  useEffect(() => {
    loginWithTokenRef.current = loginWithToken;
  }, [loginWithToken]);

  const handleCallback = useCallback(async () => {
    if (hasProcessed.current) {
      return;
    }
    hasProcessed.current = true;

    try {
      console.log('[AuthCallback] Processing OAuth callback...');

      // Get token from URL parameters
      const token = searchParams.get('token');
      const errorParam = searchParams.get('error');

      console.log('[AuthCallback] Token present:', !!token);
      console.log('[AuthCallback] Error param:', errorParam);

      if (errorParam) {
        // Handle error from backend
        let errorMessage = 'Authentication failed';

        switch (errorParam) {
          case 'auth_failed':
            errorMessage = 'Google authentication failed. Please try again.';
            break;
          case 'server_error':
            errorMessage = 'Server error occurred. Please try again later.';
            break;
          default:
            errorMessage = 'An error occurred during authentication.';
        }

        console.error('[AuthCallback] OAuth error:', errorMessage);
        setError(errorMessage);
        setProcessing(false);

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
        return;
      }

      if (!token) {
        console.error('[AuthCallback] No token in URL');
        setError('No authentication token received');
        setProcessing(false);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
        return;
      }

      // Login with token
      console.log('[AuthCallback] Logging in with token...');
      await loginWithTokenRef.current(token);
      console.log('[AuthCallback] Login successful, navigating to home');
      navigate('/', { replace: true });

    } catch (err) {
      console.error('[AuthCallback] OAuth callback error:', err);
      setError('Failed to complete authentication. Please try again.');
      setProcessing(false);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    }
  }, [navigate, searchParams]);

  useEffect(() => {
    handleCallback();
  }, [handleCallback]);

  return (
    <div className="auth-callback-page">
      <div className="auth-callback-container">
        {processing ? (
          <>
            <div className="spinner"></div>
            <h2>Completing sign in...</h2>
            <p>Please wait while we verify your credentials</p>
          </>
        ) : error ? (
          <>
            <div className="error-icon">⚠️</div>
            <h2>Authentication Error</h2>
            <p>{error}</p>
            <p className="redirect-message">Redirecting to login...</p>
          </>
        ) : (
          <>
            <div className="success-icon">✓</div>
            <h2>Success!</h2>
            <p>Redirecting to dashboard...</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
